import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User } from "firebase/auth";
import Logo from "./Logo";
import { primaryBtn } from "../styles";
import { useIsMobile } from "../hooks/useIsMobile";
import type { AuthModalMode } from "./AuthModal";

interface Props {
  children: React.ReactNode;
  user?: User | null;
  displayName?: string;
  onSignIn?: (mode?: AuthModalMode) => void;
}

const HEADER_H = 60;

/**
 * Bespoke shell for the marketing home page — no app sidebar/nav, just a
 * header (sign-in/sign-up CTAs for visitors, a "My Leagues" link for
 * members) and a plain footer. This is what the site logo always links to.
 */
export default function MarketingShell({ children, user, displayName, onSignIn }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const avatarLetter = displayName ? displayName.charAt(0).toUpperCase() : "?";

  return (
    <div style={{ minHeight: "100vh", background: "var(--color-bg)", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          height: HEADER_H,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          padding: isMobile ? "0 1rem" : "0 1.5rem",
          borderBottom: "1px solid var(--color-border)",
          boxSizing: "border-box",
        }}
      >
        <Link to="/" style={{ textDecoration: "none" }}>
          <Logo showWordmark={!isMobile} />
        </Link>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: isMobile ? "0.6rem" : "1rem" }}>
          {user ? (
            <>
              <div style={{
                width: 30, height: 30, borderRadius: "50%",
                background: "var(--color-accent)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 700, fontSize: "0.8rem",
                color: "var(--color-btn-primary-text)",
                flexShrink: 0,
              }}>
                {avatarLetter}
              </div>
              <button
                onClick={() => navigate("/overview")}
                style={{ ...primaryBtn, fontSize: "0.85rem", padding: "0.5rem 1.1rem" }}
              >
                {t("landing.header.myLeagues")} →
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onSignIn?.("signin")}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: "0.88rem", fontWeight: 500, color: "var(--color-text-muted)",
                  padding: "0.4rem 0.25rem",
                }}
              >
                {t("landing.header.signIn")}
              </button>
              <button
                onClick={() => onSignIn?.("register")}
                style={{ ...primaryBtn, fontSize: "0.85rem", padding: "0.5rem 1.1rem" }}
              >
                {t("landing.header.playFree")} →
              </button>
            </>
          )}
        </div>
      </header>

      <main style={{ flex: 1 }}>
        {children}
      </main>

      <footer
        style={{
          padding: isMobile ? "1.25rem 1rem" : "1.25rem 1.5rem",
          borderTop: "1px solid var(--color-border)",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: "center",
          gap: isMobile ? "0.35rem" : "0.5rem",
          fontSize: "0.78rem",
          color: "var(--color-text-dim)",
          textAlign: "center",
        }}
      >
        <span>{t("landing.footer.copyright")}</span>
        {!isMobile && <span>·</span>}
        <span>{t("landing.footer.free")}</span>
        {!isMobile && <span>·</span>}
        <Link to="/about" style={{ color: "var(--color-text-dim)" }}>
          {t("landing.footer.about")}
        </Link>
      </footer>
    </div>
  );
}
